import {
  DestroyRef,
  Injectable,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TenantConfiguration } from '@shared/types/clients.types';
import { ClientsApiService } from '@services/clients-api.service';
import { UserAuthorizationService } from '@services/user-authorization.service';
import { FeatureFlagsService } from '@services/feature-flags.service';
import { app_feature_flags } from '@shared/types/feature-flags.types';
import {
  analytics_report_type,
  clientMiscPermissions,
  common_strings,
} from '@shared/strings/common.strings';
import {
  CONNECTION_REQUIRING_MISC_KEYS,
  ConnectionRequiringMiscKey,
} from '@shared/constants/connection-destinations.constants';

/**
 * Global store for tenant configuration and permissions
 * This service is the SINGLE SOURCE OF TRUTH for all permission checks
 *
 * Key Features:
 * - Manages tenant permissions from API
 * - Automatically handles Super Admin bypass (super admins have all permissions)
 * - Provides computed signals for common permission checks
 * - Signal-based reactivity for automatic UI updates
 *
 * The configuration is loaded from API on login and page refresh,
 * stored in-memory via Angular signals (no localStorage needed)
 */
@Injectable({
  providedIn: 'root',
})
export class TenantConfigurationService {
  private clientsApiService = inject(ClientsApiService);
  private userAuthorizationService = inject(UserAuthorizationService);
  private featureFlagsService = inject(FeatureFlagsService);
  private destroyRef = inject(DestroyRef);

  // Tenant configuration state (in-memory global store)
  private tenantConfigSignal = signal<TenantConfiguration | null>(null);
  private isLoadingSignal = signal<boolean>(false);
  private errorSignal = signal<string | null>(null);

  // Expose read-only signals
  readonly tenantConfig = this.tenantConfigSignal.asReadonly();
  readonly isLoading = this.isLoadingSignal.asReadonly();
  readonly error = this.errorSignal.asReadonly();

  // Computed signals for common permission checks
  // readonly canExport = computed(() =>
  //   this.hasPermission(clientMiscPermissions.exports.id),
  // );

  // readonly canAccessAnalytics = computed(() =>
  //   this.hasPermission(clientMiscPermissions.analytics.id),
  // );

  // readonly canManageUsers = computed(() =>
  //   this.hasPermission(clientMiscPermissions.userManagement.id),
  // );

  // Computed signals for tenant info
  readonly organizationName = computed(
    () => this.tenantConfigSignal()?.organizationName ?? '',
  );

  readonly tenantName = computed(
    () => this.tenantConfigSignal()?.tenantName ?? '',
  );

  readonly permissions = computed(() => this.tenantConfigSignal()?.misc ?? []);

  /**
   * Load tenant configuration from the API (fire-and-forget)
   * This method handles everything internally - no need to subscribe
   * @param tenantId - The tenant ID to load configuration for
   */
  loadTenantConfiguration(tenantId: string): void {
    if (!tenantId) {
      this.errorSignal.set('Tenant ID is required');
      return;
    }

    this.isLoadingSignal.set(true);
    this.errorSignal.set(null);

    this.clientsApiService
      .getTenantConfiguration(tenantId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: config => {
          this.tenantConfigSignal.set(config);
          this.isLoadingSignal.set(false);
        },
        error: (error: { message?: string } | null) => {
          this.errorSignal.set(
            error?.message ||
              common_strings.failed_to_load_tenant_configuration,
          );
          this.isLoadingSignal.set(false);
        },
      });
  }

  /**
   * Check if user has a specific permission
   * Super Admins automatically have ALL permissions
   * @param permissionId - The permission ID to check
   * @returns true if user has the permission (or is super admin)
   */
  hasPermission(permissionId: string): boolean {
    // Super Admins have all permissions
    if (this.userAuthorizationService.isSuperAdmin) {
      return true;
    }

    return this.permissionCheck(permissionId);
  }

  /**
   * Check if user has ALL of the specified permissions
   * Super Admins automatically have ALL permissions
   * @param permissionIds - Array of permission IDs to check
   * @returns true if user has all permissions (or is super admin)
   */
  // hasAllPermissions(permissionIds: string[]): boolean {
  //   // Super Admins have all permissions
  //   if (this.userAuthorizationService.isSuperAdmin) {
  //     return true;
  //   }
  //   return permissionIds.every(id => this.hasPermission(id));
  // }

  /**
   * Check if user has ANY of the specified permissions
   * Super Admins automatically have ALL permissions
   * @param permissionIds - Array of permission IDs to check
   * @returns true if user has at least one permission (or is super admin)
   */
  // hasAnyPermission(permissionIds: string[]): boolean {
  //   // Super Admins have all permissions
  //   if (this.userAuthorizationService.isSuperAdmin) {
  //     return true;
  //   }
  //   return permissionIds.some(id => this.hasPermission(id));
  // }

  /**
   * Check if user has access to a specific route
   * Uses feature configuration to determine required permissions
   * Super Admins automatically have access to all routes
   * @param routePermission - The route permission ID to check.
   * @returns true if user has access to the route
   */
  hasRouteAccess(routePermission: string | undefined): boolean {
    if (!routePermission) {
      return true;
    }

    // Super Admins have access to everything
    if (this.userAuthorizationService.isSuperAdmin) {
      return true;
    }

    return this.permissionCheck(routePermission);
  }

  /**
   * Whether saved reports and report statistics should be fetched.
   * Super admins always have access regardless of tenant misc permissions.
   */
  hasSavedReportsAccess(): boolean {
    if (this.userAuthorizationService.isSuperAdmin) {
      return true;
    }

    const config = this.tenantConfigSignal();
    return !!(
      config?.misc.emailAnalytics?.id || config?.misc.websiteAnalytics?.id
    );
  }

  /**
   * Report type filter for saved reports and statistics API calls.
   * Returns undefined to fetch all report types when both are available or user is super admin.
   */
  getSavedReportsReportTypeFilter(): string | undefined {
    if (this.userAuthorizationService.isSuperAdmin) {
      return undefined;
    }

    const config = this.tenantConfigSignal();
    if (config?.misc.emailAnalytics?.id && config?.misc.websiteAnalytics?.id) {
      return undefined;
    }

    if (config?.misc.websiteAnalytics?.id) {
      return analytics_report_type.websiteReportId;
    }

    return analytics_report_type.emailReportId;
  }

  /**
   * Clear the tenant configuration
   * Call this when switching tenants or logging out
   */
  clearConfiguration(): void {
    this.tenantConfigSignal.set(null);
    this.errorSignal.set(null);
    this.isLoadingSignal.set(false);
  }

  /**
   * Manually set tenant configuration
   * Useful for testing or when configuration is provided from another source
   * @param config - The tenant configuration to set
   */
  setConfiguration(config: TenantConfiguration): void {
    this.tenantConfigSignal.set(config);
    this.errorSignal.set(null);
  }

  private isWebsiteAnalyticsFeatureEnabled(): boolean {
    return this.featureFlagsService.isEnabled(
      app_feature_flags.website_analytics,
    );
  }

  /**
   * Checks whether the loaded tenant misc configuration contains the requested permission.
   * @param permissionId - Client misc permission key to evaluate.
   * @returns True when the permission is enabled for the active tenant.
   */
  permissionCheck(permissionId: string): boolean {
    const config = this.tenantConfigSignal();
    if (!config?.misc) {
      return false;
    }

    switch (permissionId) {
      case clientMiscPermissions.exports.id:
        return !!config?.misc.allowDataExports?.id;
      case clientMiscPermissions.userManagement.id:
        return !!config?.misc.allowUserManagement?.id;
      case clientMiscPermissions.analytics.id:
        return !!(
          config?.misc.emailAnalytics?.id ||
          config?.misc.emailDashboard?.id ||
          (this.isWebsiteAnalyticsFeatureEnabled() &&
            config?.misc.websiteAnalytics?.id)
        );
      case clientMiscPermissions.emailAnalytics.id:
        return !!config?.misc.emailAnalytics?.id;
      case clientMiscPermissions.emailDashboard.id:
        return !!config?.misc.emailDashboard?.id;
      case clientMiscPermissions.websiteAnalytics.id:
        return (
          this.isWebsiteAnalyticsFeatureEnabled() &&
          !!config?.misc.websiteAnalytics?.id
        );
      case clientMiscPermissions.reports.id:
        return !!(
          config?.misc.emailAnalytics?.id || config?.misc?.websiteAnalytics?.id
        );
      case clientMiscPermissions.dataExplorer.id:
        return !!config?.misc.dataExplorer?.id;
      case clientMiscPermissions.connections.id:
        return CONNECTION_REQUIRING_MISC_KEYS.some(
          key => !!config.misc[key]?.id,
        );
      case clientMiscPermissions.lookalike.id:
        return !!config?.misc.allowLookalike?.id;
      default:
        return false;
    }
  }

  /**
   * Whether a connection-requiring destination is enabled on the tenant misc config.
   * Used to show per-destination cards on the Connections page (no Super Admin bypass).
   * @param key - Misc key for a destination that requires Connections (e.g. `meta`).
   * @returns True when that misc entry has an id on the loaded tenant configuration.
   */
  hasConnectionDestination(key: ConnectionRequiringMiscKey): boolean {
    return !!this.tenantConfigSignal()?.misc[key]?.id;
  }
}
